/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    CashBoxesListRequestParams,
    CashBoxesListDto,
    CashBoxDetailsDto,
    BlockCashBoxQueryParams,
    UnblockCashBoxQueryParams,
    CashBoxStatusesHistoryQueryParams,
    CashBoxStatusesHistoryDto,
    CashBoxTimeDeltaHistoryQueryParams,
    CashBoxTimeDeltaHistoryDto,
    CashBoxStateHistoryQueryParams,
    CashBoxStateHistoryDto,
    CashBoxAuditHistoryQueryParams,
    CashBoxReplacementHistoryDto,
    CashBoxAlertsHistoryQueryParams,
    CashBoxAlertsHistoryDto,
    CashBoxAlertsHistoryExcelParams,
    CashBoxesMapSearchParams,
    CashBoxesMap,
    CashBoxDetailsForMap,
    CashBoxeState,
    CashboxLocationData,
    HistoryLocationsQueryParams,
    HistoryLocationsList,
    HistoryLocationsFilterParams,
    GpsTrackingLocationsList,
    CashBoxesListExcelRequestParams,
    CashBoxesListFilterParams,
    CheckSumsHistoryDto,
    CashBoxAuditHistoryDto,
    ChangeCashBoxStatusParams,
    CashBoxLastDocument,
} from './cashBoxesClient.typings';
import apiClient from '../apiClient';
import { LoadCashBoxDocsFileParams } from './cashBoxesClient.typings';
import {
    DATA_UNAVAILABLE,
    cutEmptyParams,
    cutEmptyParamsIncludingArrays,
    makeArrayFromCommaSeparatedString,
} from '../../../utils/utils';
import {
    PageQueryParams,
    PeriodQueryParams,
    RegistryTurnoversByPeriodDto,
    TurnoversByPeriodEXCELParams,
    TurnoversByPeriodQueryParams,
} from '../apiCommon.typings';
import moment from 'moment';
import { CashBoxesListTableData } from '../../../components/RegistryWidgets/CashBoxes/CashBoxesList/CashBoxesListConfig';
import { RequestToCreateExcelDto } from '../downloadings/downloadingsClient.typings';

const transformDayRangeValueToDate = (value: number | string, inFuture = false) => {
    if (value === null) return null;
    if (typeof value === 'number' && inFuture) return moment().utc().add(value, 'day').format();
    if (typeof value === 'number') return moment().utc().subtract(value, 'day').format();
    else return value;
};

const SEARCH_TYPE_TO_SEARCH_PARAM: { [key in keyof CashBoxesListTableData]?: string } = {
    taxNumber: 'cashBoxTaxNumbers',
    cashRegisterFactoryNumber: 'cashRegisterFactoryNumbers',
    storeTaxNumber: 'storeTaxNumbers',
    companyTaxNumber: 'companyTaxNumbers',
};

const getSearchParamName = (searchType: keyof CashBoxesListTableData): string => {
    let searchParam = SEARCH_TYPE_TO_SEARCH_PARAM[searchType];
    if (searchParam === undefined) searchParam = DATA_UNAVAILABLE;
    return searchParam;
};

const adjustCashBoxesListFilterParams = (params: CashBoxesListFilterParams) => {
    const { search_type, search_text, ...newParams } = params;

    if (newParams.state !== CashBoxeState.OFFLINE) {
        delete newParams.fromOffline;
        delete newParams.toOffline;
    }

    const softwareVersions = newParams.softwareVersions.map((item) => item.name);
    delete newParams.softwareVersions;

    let online: boolean = null;
    if (newParams.state !== null) online = newParams.state === CashBoxeState.ONLINE;
    delete newParams.state;

    const fromLastPingDateAtUtc = transformDayRangeValueToDate(newParams.toOffline);
    const toLastPingDateAtUtc = transformDayRangeValueToDate(newParams.fromOffline);
    delete newParams.fromOffline;
    delete newParams.toOffline;

    const fromLastDocumentDateAtUtc = transformDayRangeValueToDate(newParams.toLastDocument);
    const toLastDocumentDateAtUtc = transformDayRangeValueToDate(newParams.fromLastDocument);
    delete newParams.fromLastDocument;
    delete newParams.toLastDocument;

    if (params.fromExpiredCert === null && params.toExpiredCert) {
        params.fromExpiredCert = 0;
    }
    const fromCertificateEndDateAtUtc = transformDayRangeValueToDate(newParams.fromExpiredCert, true);
    const toCertificateEndDateAtUtc = transformDayRangeValueToDate(newParams.toExpiredCert, true);
    delete newParams.fromExpiredCert;
    delete newParams.toExpiredCert;

    return cutEmptyParamsIncludingArrays({
        [getSearchParamName(search_type as keyof CashBoxesListTableData)]: makeArrayFromCommaSeparatedString(
            search_text,
            true,
        ),
        online,
        fromLastPingDateAtUtc,
        toLastPingDateAtUtc,
        fromLastDocumentDateAtUtc,
        toLastDocumentDateAtUtc,
        fromCertificateEndDateAtUtc,
        toCertificateEndDateAtUtc,
        softwareVersions,
        ...newParams,
    });
};

/** The actual wire shape sent to `1.0.0/cashboxes` / `1.0.0/cashboxes/excel`, derived directly
 * from adjustCashBoxesListFilterParams so it can't drift from what the client really sends. */
export type CashBoxesListWireBody = ReturnType<typeof adjustCashBoxesListFilterParams>;

async function listCashBoxes(params: CashBoxesListRequestParams): Promise<CashBoxesListDto> {
    return apiClient.post<CashBoxesListDto>('1.0.0/cashboxes', adjustCashBoxesListFilterParams(params));
}

async function downloadCashBoxesAsExcel(params: CashBoxesListExcelRequestParams): Promise<RequestToCreateExcelDto> {
    const queryParams = adjustCashBoxesListFilterParams(params);

    return apiClient.post<RequestToCreateExcelDto>('1.0.0/cashboxes/excel', queryParams);
}

async function getCashBox(taxNumber: string): Promise<CashBoxDetailsDto> {
    return apiClient.get<CashBoxDetailsDto>(`1.0.0/cashboxes/${taxNumber}`);
}

async function downloadCashBoxDocsFile(params: LoadCashBoxDocsFileParams): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashboxes/${params.taxNumber}/documents/excel`, cutEmptyParams(params));
}

async function getCashBoxLastDocument(cashBoxTaxNumber: string): Promise<CashBoxLastDocument> {
    return apiClient.get<CashBoxLastDocument>(`1.0.0/cashboxes/${cashBoxTaxNumber}/lastDocument`);
}

async function changeCashBoxStatus(taxNumber: string, params: ChangeCashBoxStatusParams): Promise<{}> {
    return apiClient.post<string>(`1.0.0/cashboxes/${taxNumber}/status`, params);
}

async function blockCashBox(taxNumber: string, params: BlockCashBoxQueryParams): Promise<string> {
    return apiClient.post<string>(`1.0.0/cashboxes/${taxNumber}/block`, params);
}

async function unblockCashBox(taxNumber: string, params: UnblockCashBoxQueryParams): Promise<string> {
    return apiClient.post<string>(`1.0.0/cashboxes/${taxNumber}/unblock`, params);
}

async function removeBlockUnblockSchedule(taxNumber: string): Promise<string> {
    return apiClient.delete<string>(`1.0.0/cashboxes/${taxNumber}/blocks`);
}

async function getCashBoxPeriodTurnovers(
    taxNumber: string,
    params: TurnoversByPeriodQueryParams,
): Promise<RegistryTurnoversByPeriodDto[]> {
    const { ...newParams } = params;
    delete newParams.group;
    return apiClient.get<RegistryTurnoversByPeriodDto[]>(
        `1.0.0/cashboxes/${taxNumber}/turnovers`,
        cutEmptyParams(newParams),
    );
}

async function getCashBoxStatusesHistory(
    taxNumber: string,
    params: CashBoxStatusesHistoryQueryParams,
): Promise<CashBoxStatusesHistoryDto> {
    return apiClient.get<CashBoxStatusesHistoryDto>(`1.0.0/cashboxes/${taxNumber}/statusesHistory`, params);
}

async function downloadCashBoxStatusHistoryAsExcel(taxNumber: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashboxes/${taxNumber}/statusesHistory/excel`);
}

async function getCashBoxTimeDeltaHistory(
    taxNumber: string,
    params: CashBoxTimeDeltaHistoryQueryParams,
): Promise<CashBoxTimeDeltaHistoryDto> {
    return apiClient.get<CashBoxTimeDeltaHistoryDto>(`1.0.0/cashboxes/${taxNumber}/timeDeltaHistory`, params);
}

async function downloadCashBoxTimeDeltaHistoryAsExcel(
    taxNumber: string,
    params: PeriodQueryParams,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashboxes/${taxNumber}/timeDeltaHistory/excel`, cutEmptyParams(params));
}

async function getCashBoxStateHistory(
    taxNumber: string,
    params: CashBoxStateHistoryQueryParams,
): Promise<CashBoxStateHistoryDto> {
    return apiClient.get<CashBoxStateHistoryDto>(`1.0.0/cashboxes/${taxNumber}/statesHistory`, params);
}

async function downloadCashBoxStateHistoryAsExcel(taxNumber: string, params: PeriodQueryParams): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashboxes/${taxNumber}/statesHistory/excel`, cutEmptyParams(params));
}

async function getCashBoxAuditHistory(
    taxNumber: string,
    params: CashBoxAuditHistoryQueryParams,
): Promise<CashBoxAuditHistoryDto> {
    return apiClient.get<CashBoxAuditHistoryDto>(`1.0.0/cashboxes/${taxNumber}/auditHistory`, params);
}

async function downloadCashBoxAuditHistoryAsExcel(taxNumber: string, params: PeriodQueryParams): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashboxes/${taxNumber}/auditHistory/excel`, cutEmptyParams(params));
}

async function getCashBoxReplacementHistory(taxNumber: string): Promise<CashBoxReplacementHistoryDto> {
    return apiClient.get<CashBoxReplacementHistoryDto>(`1.0.0/cashboxes/${taxNumber}/replacements`);
}

async function downloadCashBoxReplacementHistoryAsExcel(taxNumber: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashboxes/${taxNumber}/replacements/excel`);
}

async function getCashBoxAlertsHistory(
    taxNumber: string,
    params: CashBoxAlertsHistoryQueryParams,
): Promise<CashBoxAlertsHistoryDto> {
    return apiClient.get<CashBoxAlertsHistoryDto>(`1.0.0/alerts/cashboxes/${taxNumber}`, params);
}

async function downloadCashBoxAlertsHistoryAsExcel(
    taxNumber: string,
    params: CashBoxAlertsHistoryExcelParams,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/alerts/cashboxes/${taxNumber}/excel`, cutEmptyParams(params));
}

async function downloadCashBoxTurnoversAsExcel(
    taxNumber: string,
    params: TurnoversByPeriodEXCELParams,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashboxes/${taxNumber}/turnovers/excel`, cutEmptyParams(params));
}

async function getCashBoxesForMap(params: CashBoxesMapSearchParams): Promise<CashBoxesMap> {
    const { search_type, search_text, afterKeyId, limit, cashBoxStatuses } = params.filterParams;
    const { viewport } = params;
    const { bounds, zoom } = viewport;

    delete params.filterParams;

    const body = {
        bounds,
        zoom,
        afterKeyId,
        limit,
        cashBoxStatuses,
        [search_type]: makeArrayFromCommaSeparatedString(search_text, true),
    };

    return apiClient.post<CashBoxesMap>('1.0.0/map/cashBoxes', cutEmptyParamsIncludingArrays(body));
}

async function getCashBoxDetailsForMap(taxNumber: string): Promise<CashBoxDetailsForMap> {
    return apiClient.get<CashBoxDetailsForMap>(`1.0.0/map/cashboxes/${taxNumber}`);
}

async function getCashBoxLocationData(taxNumber: string): Promise<CashboxLocationData> {
    return apiClient.get<CashboxLocationData>(`1.0.0/cashboxes/${taxNumber}/gps`);
}

async function getHistoryLocationsList(
    taxNumber: string,
    params: HistoryLocationsQueryParams,
): Promise<HistoryLocationsList> {
    return apiClient.get<HistoryLocationsList>(`1.0.0/cashboxes/${taxNumber}/gpsHistory`, cutEmptyParams(params));
}

async function getGpsTrackingList(
    taxNumber: string,
    params: HistoryLocationsFilterParams,
): Promise<GpsTrackingLocationsList> {
    return apiClient.get<GpsTrackingLocationsList>(`1.0.0/cashboxes/${taxNumber}/gpsTracking`, cutEmptyParams(params));
}

async function getCheckSumsHistory(taxNumber: string, params: PageQueryParams): Promise<CheckSumsHistoryDto> {
    return apiClient.get<CheckSumsHistoryDto>(`1.0.0/cashboxes/${taxNumber}/checksums`, params);
}

export const cashboxesClient = {
    listCashBoxes,
    getCashBox,
    downloadCashBoxDocsFile,
    getCashBoxLastDocument,
    changeCashBoxStatus,
    blockCashBox,
    unblockCashBox,
    removeBlockUnblockSchedule,
    downloadCashBoxesAsExcel,
    getCashBoxPeriodTurnovers,
    getCashBoxStatusesHistory,
    downloadCashBoxStatusHistoryAsExcel,
    getCashBoxTimeDeltaHistory,
    downloadCashBoxTimeDeltaHistoryAsExcel,
    getCashBoxStateHistory,
    downloadCashBoxStateHistoryAsExcel,
    getCashBoxAuditHistory,
    downloadCashBoxAuditHistoryAsExcel,
    getCashBoxReplacementHistory,
    downloadCashBoxReplacementHistoryAsExcel,
    getCashBoxAlertsHistory,
    downloadCashBoxAlertsHistoryAsExcel,
    downloadCashBoxTurnoversAsExcel,
    getCashBoxesForMap,
    getCashBoxDetailsForMap,
    getCashBoxLocationData,
    getHistoryLocationsList,
    getGpsTrackingList,
    getCheckSumsHistory,
};
